// src/components/ws/useColWidths.jsx
// 表の列の幅を、エクセルのように見出しの境目をドラッグして変える【2026-10-13】
//
//   ・見出しの右端（境目）にカーソルを当てると ↔ になる。ドラッグで幅が変わる
//   ・境目をダブルクリックすると、その列だけ元の幅に戻る
//   ・最初に動かした時点で、全部の列の今の幅を測って固定する（table-layout: fixed）。
//     そうしないと、ほかの列が勝手に伸び縮みして、狙った幅にならない
//   ・幅はこのブラウザに覚える（表ごと）。「列の幅を戻す」で全部元に戻る
//
//   使い方:
//     const cols = useColWidths('matrix', keys, { defaultWidth: k => ... });
//     <table ref={cols.tableRef} className={cols.className} style={cols.tableStyle}>
//       {cols.colgroup}
//       <th style={cols.thStyle(key)}>見出し<ColGrip cols={cols} k={key} /></th>
//   keys は列の並び（同じ key を何度使ってもよい。同じ key の列は同じ幅になる）
import React, { useCallback, useMemo, useRef, useState } from 'react';

const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } };
const write = (k, v) => { try { if (v) localStorage.setItem(k, JSON.stringify(v)); else localStorage.removeItem(k); } catch (e) { /* 動く */ } };
const MIN = 36;

export function useColWidths(id, keys, opts = {}) {
  const storeKey = 'wsCols.' + id;
  const [widths, setWidths] = useState(() => read(storeKey));   // { key: px } | null
  const [dragKey, setDragKey] = useState(null);
  const tableRef = useRef(null);
  const fallback = opts.defaultWidth || (() => 120);

  const measure = useCallback(() => {
    const t = tableRef.current;
    const out = {};
    if (!t) return out;
    const ths = t.querySelectorAll('thead tr:first-child > th');
    ths.forEach((th, i) => {
      const k = keys[i];
      if (k != null && out[k] == null) out[k] = Math.round(th.getBoundingClientRect().width);
    });
    return out;
  }, [keys]);

  const widthOf = (k) => (widths && widths[k] != null ? widths[k] : fallback(k));
  const active = !!widths;

  const start = (e, k) => {
    if (e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    const base = { ...measure(), ...(widths || {}) };
    const th = e.currentTarget.parentElement;
    const startX = e.clientX;
    const startW = base[k] != null ? base[k] : Math.round(th.getBoundingClientRect().width);
    let cur = { ...base, [k]: startW };
    setWidths(cur); setDragKey(k);
    document.body.classList.add('ws-col-resizing');
    const move = (ev) => {
      cur = { ...cur, [k]: Math.max(MIN, Math.round(startW + ev.clientX - startX)) };
      setWidths(cur);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      document.body.classList.remove('ws-col-resizing');
      setDragKey(null);
      write(storeKey, cur);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  /** その列だけ元に戻す（ほかの列は今の幅のまま） */
  const resetOne = (k) => {
    if (!widths) return;
    const next = { ...widths };
    delete next[k];
    // 1列だけ「元の幅」に戻すため、その列は測り直す
    setWidths(null);
    requestAnimationFrame(() => {
      const m = measure();
      const merged = { ...next, [k]: m[k] != null ? m[k] : fallback(k) };
      setWidths(merged); write(storeKey, merged);
    });
  };
  const resetAll = () => { setWidths(null); write(storeKey, null); };

  const tableStyle = active
    ? { tableLayout: 'fixed', width: keys.reduce((a, k) => a + widthOf(k), 0), minWidth: 0 }
    : undefined;
  const colgroup = active ? (
    <colgroup>{keys.map((k, i) => <col key={i} style={{ width: widthOf(k) }} />)}</colgroup>
  ) : null;
  const thStyle = () => ({ position: 'sticky' });   // 見出しは sticky のまま（つまみの位置の基準になる）

  return useMemo(() => ({
    tableRef, active, widthOf, start, resetOne, resetAll, tableStyle, colgroup, thStyle, dragKey,
    className: active ? 'ws-colfixed' : '',
  }), [widths, dragKey, keys]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** 見出しの右端に置く、幅を変えるつまみ */
export function ColGrip({ cols, k, label }) {
  return (
    <span className={'ws-col-grip' + (cols.dragKey === k ? ' on' : '')} role="separator" aria-orientation="vertical"
          aria-label={(label || '列') + 'の幅（ドラッグで変える。ダブルクリックで元に戻す）'}
          title="ドラッグで列の幅を変える（ダブルクリックで元に戻す）"
          onPointerDown={(e) => cols.start(e, k)}
          onMouseDown={(e) => e.preventDefault()}
          onDoubleClick={(e) => { e.stopPropagation(); cols.resetOne(k); }}
          onClick={(e) => e.stopPropagation()} />
  );
}
